import { Icon } from '@rbilabs/universal-components';
import { Pressable, StyleSheet, View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { BottomSheet } from 'components/bottom-sheet';
import { Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';
import { WEB_EXTRA_SMALL_MAX_WIDTH } from 'utils/constants';
import { isMobile } from 'utils/environment';

import { AboutBadgesLogo } from './about-badges-logo';

export const AboutBadgesModal = ({ onClose }: { onClose: () => void }) => {
  const descriptionLines = [t('whenYouHitCertainMilestones'), t('moreYouDoTheMoreYouUnlock')];

  return (
    <BottomSheet isOpen onOpenEventMessage="Open about badges modal" onClose={onClose}>
      <View style={styles.wrapper}>
        <View style={styles.header}>
          <Typography variant="headerOne" accessibilityLevel={2} style={styles.mainHeaderText}>
            {t('bkBadges')}
          </Typography>
          {!isMobile() && (
            <Pressable
              onPress={onClose}
              accessible
              accessibilityRole="button"
              accessibilityLabel={t('close')}
              style={styles.closeButton}
            >
              <Icon variant="cancel" title={t('cancel')} />
            </Pressable>
          )}
        </View>
        <View style={styles.container}>
          <Typography style={styles.subtitle}>
            {t('yourWayToShowOff', 'Your way to show off just how much you rule!')}
          </Typography>
          {/* @ts-ignore - style type mismatch on web */}
          <AboutBadgesLogo style={styles.image} />
          <View style={[styles.descriptionSection, !isMobile() && styles.descriptionSectionWeb]}>
            <Typography variant="headerOne" accessibilityLevel={3} style={styles.subHeaderText}>
              {t('howItWorksLower')}:
            </Typography>
            {descriptionLines.map((line, idx) => (
              <View key={idx} style={styles.descriptionLine}>
                <View style={styles.descriptionLineNumberContainer}>
                  <Typography style={styles.descriptionLineNumber}>{idx + 1}</Typography>
                </View>
                <Typography style={styles.descriptionLineText}>{line}</Typography>
              </View>
            ))}
          </View>
          <ActionButton
            variant={ActionButtonVariants.OUTLINE}
            onPress={onClose}
            alignSelf="stretch"
          >
            {t('letsGo')}
          </ActionButton>
        </View>
      </View>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    backgroundColor: primitive.$white,
    alignItems: 'center',
  },
  container: {
    alignItems: 'center',
    paddingBottom: spacing.$6,
    width: '100%',
    maxWidth: WEB_EXTRA_SMALL_MAX_WIDTH,
  },
  header: {
    flexDirection: 'row',
    position: 'relative',
    justifyContent: 'center',
    width: '100%',
    marginTop: spacing.$4,
  },
  mainHeaderText: {
    marginBottom: spacing.$1,
    fontSize: primitive.fontSizes['2xl'],
    lineHeight: primitive.fontSizes['3xl'],
  },
  subHeaderText: {
    margin: 0,
    fontSize: primitive.fontSizes.md,
    textAlign: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  subtitle: {
    fontSize: primitive.fontSizes.xs,
    textAlign: 'center',
    marginBottom: spacing.$3,
  },
  image: {
    width: 113,
    height: 75,
    marginBottom: spacing.$5,
  },
  descriptionSection: {
    gap: spacing.$4,
    paddingHorizontal: spacing.$6,
    width: '100%',
    marginBottom: spacing.$8,
  },
  descriptionSectionWeb: {
    alignSelf: 'center',
  },
  descriptionLine: {
    flexDirection: 'row',
    gap: spacing.$2,
    alignItems: 'flex-start',
  },
  descriptionLineNumberContainer: {
    width: spacing.$6,
    height: spacing.$6,
    backgroundColor: primitive.$bbqBrown,
    borderRadius: spacing.$3,
    justifyContent: 'center',
    alignItems: 'center',
  },
  descriptionLineNumber: {
    fontSize: primitive.fontSizes.xs,
    color: primitive.$white,
  },
  descriptionLineText: {
    flex: 1,
    fontSize: primitive.fontSizes.xs,
  },
});
